import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  CreditCardIcon,
  FileTextIcon,
  SettingsIcon,
  BarChartIcon,
  DollarSignIcon,
  ExternalLinkIcon,
  InfoIcon,
} from "lucide-react";
import ModelPricing from "./ModelPricing";
import { BillingService } from "../../services/BillingService";
import { CreditBalance, PaymentMethod } from "../../model_gen/billing";
import { usePermissions } from "../../PermissionsProvider";
import { Skeleton } from "../ui/skeleton";
import AddCreditsModal from "./AddCreditsModal";
import AutoTopupModal from "./AutoTopupModal";
import { CompanyService } from "../../services/CompanyService";
import { Company } from "../../model_gen/company";
import SubscriptionPlans from "./SubscriptionPlans";

interface BillingOverviewProps {
  onTabChange: (key: string) => void;
}

const BillingOverview: React.FC<BillingOverviewProps> = ({ onTabChange }) => {
  const { apiKey } = usePermissions();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddCreditsModal, setShowAddCreditsModal] =
    useState<boolean>(false);
  const [showAutoTopupModal, setShowAutoTopupModal] = useState<boolean>(false);
  const [company, setCompany] = useState<Company | null>(null);

  const [autoTopupEnabled, setAutoTopupEnabled] = useState<boolean>(false);
  const [autoTopupThreshold, setAutoTopupThreshold] = useState<string>("10.00");
  const [autoTopupAmount, setAutoTopupAmount] = useState<string>("50.00");

  useEffect(() => {
    if (apiKey) {
      fetchCompany();
      fetchCreditBalance();
      fetchPaymentMethods();
    }
  }, [apiKey]);

  const fetchCompany = () => {
    CompanyService.getCompany(null, apiKey).subscribe(
      (result: Company) => {
        setCompany(result);
      },
      (error: any) => {
        console.error("Error fetching company:", error);
      }
    );
  };

  const fetchCreditBalance = () => {
    setLoading(true);
    setError(null);

    BillingService.getCreditBalance(null, apiKey).subscribe(
      (result: CreditBalance) => {
        setBalance(result);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching credit balance:", error);
        setError(error.message || "Failed to load credits");
        setLoading(false);
      }
    );
  };

  const fetchPaymentMethods = () => {
    BillingService.getPaymentMethods(null, apiKey).subscribe(
      (result: PaymentMethod[]) => {
        // Find the payment method with auto top-up enabled
        const autoTopupMethod = result.find((pm) => pm.auto_topup_enabled);
        if (autoTopupMethod) {
          setAutoTopupEnabled(true);
          setAutoTopupThreshold(
            autoTopupMethod.auto_topup_threshold?.toFixed(2) || "10.00"
          );
          setAutoTopupAmount(
            autoTopupMethod.auto_topup_amount?.toFixed(2) || "50.00"
          );
        }
      },
      (error: any) => {
        console.error("Error fetching payment methods:", error);
      }
    );
  };

  const handleRefreshData = () => {
    fetchCreditBalance();
    fetchPaymentMethods();
  };

  const formatCredits = (amount: number): string => {
    return amount.toLocaleString();
  };

  const dollarsToCredits = (dollars: number): number => {
    // 1 credit = $0.002, so credits = dollars / 0.002
    return Math.round(dollars / 0.002);
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Page Title */}
      <h2 className="text-2xl font-gc-demi-bold text-foreground">
        {company?.usage_based_billing ? "Pay as you go" : "Invoicing is on"}
      </h2>

      {/* Credit Balance Section */}
      {company?.usage_based_billing && (
        <div className="space-y-6">
          {/* Balance Display */}
          <div className="space-y-4">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-12 w-40" />
                <div className="flex gap-3">
                  <Skeleton className="h-10 w-48" />
                  <Skeleton className="h-10 w-32" />
                </div>
              </div>
            ) : error ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground font-gc-reg">
                    Credit balance
                  </p>
                  <InfoIcon className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-destructive">
                  Failed to load balance
                </p>
              </div>
            ) : balance ? (
              <>
                {/* Balance Label */}
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground font-gc-reg">
                    Credit balance
                  </p>
                  <InfoIcon className="w-4 h-4 text-muted-foreground cursor-help" />
                </div>

                {/* Balance Amount */}
                <p className="text-4xl font-gc-bold text-foreground">
                  {formatCredits(balance.balance)} credits
                </p>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowAddCreditsModal(true)}
                    variant="default"
                    className="font-gc-med">
                    Add to credit balance
                  </Button>
                </div>
              </>
            ) : null}
          </div>

          {/* Auto Recharge Card */}
          {!loading && !error && balance && autoTopupEnabled && (
            <Card className="border-green-600 bg-green-50 dark:bg-green-950/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-gc-med text-green-900 dark:text-green-100">
                      Auto recharge is on
                    </p>
                    <p className="text-sm text-green-800 dark:text-green-200">
                      When your credit balance reaches{" "}
                      {formatCredits(
                        dollarsToCredits(
                          autoTopupThreshold ? parseFloat(autoTopupThreshold) : 0
                        )
                      )}{" "}
                      credits, your payment method will be charged $
                      {autoTopupAmount} to bring the balance up to{" "}
                      {formatCredits(
                        dollarsToCredits(
                          autoTopupAmount ? parseFloat(autoTopupAmount) : 0
                        )
                      )}{" "}
                      credits.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowAutoTopupModal(true)}
                    variant="outline"
                    size="sm"
                    className="flex-shrink-0 bg-white dark:bg-background">
                    Modify
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Auto Top-Up Setup (when not enabled) */}
          {!loading && !error && balance && !autoTopupEnabled && (
            <Card className="border-maitai-green bg-maitai-green/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <DollarSignIcon className="w-6 h-6 text-maitai-green" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="space-y-1">
                      <p className="text-lg font-gc-med text-foreground">
                        {balance.autopay_credit_granted
                          ? "Set up auto recharge"
                          : "Set up autopay, get 2,500 credits"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {balance.autopay_credit_granted
                          ? "Your account will automatically top up when your balance is low, ensuring uninterrupted service."
                          : "Enable auto recharge and receive 2,500 credits. Your account will automatically top up when your balance is low, ensuring uninterrupted service."}
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowAutoTopupModal(true)}
                      variant="default"
                      className="font-gc-med">
                      Set up auto recharge
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Quick Actions Grid */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Payment methods */}
          <Card
            className="border-border cursor-pointer hover:bg-accent/20 transition-colors"
            onClick={() => onTabChange("paymentMethods")}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <CreditCardIcon className="w-6 h-6 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-gc-med text-foreground">
                    Payment methods
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Add or change payment method
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Billing history */}
          <Card
            className="border-border cursor-pointer hover:bg-accent/20 transition-colors"
            onClick={() => onTabChange("history")}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <FileTextIcon className="w-6 h-6 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-gc-med text-foreground">
                    Billing history
                  </p>
                  <p className="text-sm text-muted-foreground">
                    View past and current invoices
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card
            className="border-border cursor-pointer hover:bg-accent/20 transition-colors"
            onClick={() => onTabChange("preferences")}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <SettingsIcon className="w-6 h-6 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-gc-med text-foreground">
                    Preferences
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Manage billing information
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Usage limits */}
          <Card className="border-border opacity-50 cursor-not-allowed">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <BarChartIcon className="w-6 h-6 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-gc-med text-muted-foreground">
                    Usage limits
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Set monthly spend limits (coming soon)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card
            className="border-border cursor-pointer hover:bg-accent/20 transition-colors"
            onClick={() =>
              window.open("https://trymaitai.ai/pricing", "_blank")
            }>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <DollarSignIcon className="w-6 h-6 text-muted-foreground" />
                <div className="space-y-1 flex-1">
                  <p className="text-sm font-gc-med text-foreground">Pricing</p>
                  <p className="text-sm text-muted-foreground">
                    View pricing and FAQs
                  </p>
                </div>
                <ExternalLinkIcon className="w-4 h-4 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Subscription Plans */}
      <SubscriptionPlans />

      {/* Model Pricing */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Model Pricing</h3>
          <p className="text-sm text-muted-foreground">
            Current pricing for all available models
          </p>
        </div>
        <ModelPricing />
      </div>

      {/* Add Credits Modal */}
      <AddCreditsModal
        open={showAddCreditsModal}
        onClose={() => setShowAddCreditsModal(false)}
        onSuccess={fetchCreditBalance}
      />

      {/* Auto Top-Up Modal */}
      <AutoTopupModal
        open={showAutoTopupModal}
        onClose={() => setShowAutoTopupModal(false)}
        onSuccess={handleRefreshData}
        apiKey={apiKey}
        currentEnabled={autoTopupEnabled}
        currentThreshold={autoTopupThreshold}
        currentAmount={autoTopupAmount}
        autopayCreditGranted={balance?.autopay_credit_granted ?? false}
      />
    </div>
  );
};

export default BillingOverview;
